import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { SchoolsApi } from 'api';
import { Api } from 'api/cabmun-api-public';
import Chart from 'features/schools/statistics/people/Chart';
import SwitchTabs, { BlueLinksTabSmall } from 'features/shared/SwitchTabs';
import Spacer from 'features/shared/Spacer';
import LoaderComponent from 'features/shared/Loader';
import dayjs from 'dayjs';
import ruWithShortMonth from 'utils/dayjs/ruWithShortMonth';
import { useClickStreamContext } from 'features/shared/ClickStream';
import { Text } from 'features/shared/ui/Page';

export enum PeopleType {
  Students,
  Teachers,
}

interface Props {
  showTotal?: boolean;
  type: PeopleType;
  filter: number | null;
  isRegion: boolean;
}

export const Title = styled.h3`
  margin-bottom: 16px;
  font-size: 18px;
  line-height: 24px;
  text-transform: uppercase;
  color: rgba(9, 21, 38, 0.85);
`;

enum TabId {
  CumulativeTotal = 'CumulativeTotal',
  MonthlyDistribution = 'MonthlyDistribution',
}

const tabs = [
  { id: TabId.CumulativeTotal, label: 'Нарастающий итог за учебный год' },
  { id: TabId.MonthlyDistribution, label: 'Распределение по месяцам' },
];

const yTitles = {
  [TabId.CumulativeTotal]: (
    <>
      Каждый показатель на графике — это сумма результата за конкретный месяц и результатов
      <br /> за предыдущие месяцы. Например, показатель за ноябрь состоит из результатов <br />
      за ноябрь, октябрь и сентябрь.
    </>
  ),
  [TabId.MonthlyDistribution]: (
    <>
      Каждый показатель на графике — это результат только за конкретный месяц.
      <br />
      <br />
      <br />
    </>
  ),
};

const getChartData = (statistic: Api.StatisticByMonth.Total[], key: keyof Api.PeopleCountsSample) =>
  statistic.map((sample) => ({
    date: dayjs(sample.sample_date).locale('ru_short_month', ruWithShortMonth).format('MMMM ’YY'),
    value: sample[key],
  }));

function getStudentSeries(statistic: Api.StatisticByMonth.Total[], isCumulative: boolean) {
  return [
    {
      title: 'зарегистрированы',
      color: '#7B93E3',
      values: getChartData(statistic, isCumulative ? 'reg_student_cumulative' : 'reg_student_count'),
    },
    {
      title: 'занимаются на платформе',
      color: '#1CC67F',
      values: getChartData(statistic, isCumulative ? 'active_student_cumulative' : 'active_student_count'),
    },
  ];
}

function getTeacherSeries(statistic: Api.StatisticByMonth.Total[], isCumulative: boolean) {
  return [
    {
      title: 'зарегистрированы',
      color: '#EF8CDF',
      values: getChartData(statistic, isCumulative ? 'reg_teacher_cumulative' : 'reg_teacher_count'),
    },
    {
      title: 'с учениками в классе, которые занимаются на платформе',
      color: '#4D94FF',
      values: getChartData(statistic, isCumulative ? 'active_teacher_cumulative' : 'active_teacher_count'),
    },
  ];
}

const Component = ({ showTotal, type, filter, isRegion }: Props) => {
  const { logPageEvent } = useClickStreamContext();

  const [tab, setTab] = useState(tabs[0]);
  const [statistic, setStatistic] = useState<Api.StatisticByMonth>();

  const isCumulative = tab.id === TabId.CumulativeTotal;

  const series = useMemo(() => {
    if (!statistic) {
      return null;
    }
    const filteredStatistics =
      filter === null || filter === -1
        ? statistic.total
        : statistic.detailed_data.filter((el) => (isRegion ? el.subject_id : el.school_id) === filter);

    return type === PeopleType.Students
      ? getStudentSeries(filteredStatistics, isCumulative)
      : getTeacherSeries(filteredStatistics, isCumulative);
  }, [type, isCumulative, statistic, filter]);

  useEffect(() => {
    async function fetch() {
      try {
        const result = await SchoolsApi.getStatisticsByMonths();
        const total = result?.total || [];
        const detailed_data = result?.detailed_data || [];
        total.sort((a, b) => (a.sample_date < b.sample_date ? -1 : 1));
        detailed_data.sort((a, b) => (a.sample_date < b.sample_date ? -1 : 1));
        setStatistic({ total: total, detailed_data: detailed_data });
      } catch (e) {}
    }

    fetch();
  }, []);

  return !series ? (
    <div style={{ width: '100%', height: '371px' }}>
      <LoaderComponent />
    </div>
  ) : (
    <>
      <SwitchTabs
        values={tabs}
        value={tab}
        handleChange={(value) => {
          setTab(value);
          logPageEvent({
            eventType: 'schools_statistic_by_month_select_chart_type_tab_' + value.id,
            eventInfo: 'Школы|Статистика по месяцам: Выбрал тип графика ' + value.label,
          });
        }}
        tabRenderer={BlueLinksTabSmall}
        qaPrefix="schools_statistic_"
      />
      <Spacer size={16} />
      <Text>{yTitles[tab.id]}</Text>
      <Spacer size={16} />
      <Chart yTitle={type === PeopleType.Students ? 'Учеников' : 'Учителей'} showTotal={showTotal} series={series} />
    </>
  );
};

export default Component;
